/**
 * ============================================================================
 * METRICS CONFIGURATION FOR DASHBOARD SELECTION
 * ============================================================================
 * Defines which metrics are available for PC and Mobile data frames
 */

export type DataFrameType = 'pc' | 'mobile';
export type GranularityType = 'daily' | 'weekly' | 'monthly';

/**
 * No-access error identifiers for dashboard metrics APIs.
 */
export const METRIC_NO_ACCESS_CODE = 20007;
export const METRIC_NO_ACCESS_STATUS = 403;

/**
 * ============================================================================
 * ROUTING CONFIGURATION
 * ============================================================================
 * Centralized routing paths for easier maintenance
 */

/**
 * Special metric routing configuration
 * Maps specific metrics to their custom routes
 */
export const METRIC_SPECIAL_ROUTES: Record<string, string> = {
  full_game_asp: '/dashboard/game/:gameCode/monetization/salesOverview',
};

/**
 * Special comparison labels configuration
 * Metrics that use custom comparison labels instead of DoD/WoW/MoM
 */
export const METRIC_SPECIAL_COMPARISON_LABELS: Record<string, string> = {
  full_game_asp: 'Change',
};

/**
 * Generate dashboard route path based on metric key
 * Special metrics have custom routes, others go to overview
 * @param gameCode - The game code
 * @param granularity - The granularity (daily/weekly/monthly)
 * @param metricKey - Optional metric key to check for special routing
 * @returns The appropriate dashboard path
 */
export const getDashboardOverviewPath = (
  gameCode: string,
  granularity: GranularityType,
  metricKey?: string
): string => {
  // Check if metric has special route
  if (metricKey && METRIC_SPECIAL_ROUTES[metricKey]) {
    return METRIC_SPECIAL_ROUTES[metricKey].replace(':gameCode', gameCode);
  }
  
  // Default to overview route
  return `/dashboard/game/${gameCode}/overview/${granularity}`;
};

/**
 * Route paths configuration
 * Centralize all route patterns for easy updates
 */
export const ROUTE_PATHS = {
  DASHBOARD_OVERVIEW: '/dashboard/game/:gameCode/overview/:granularity',
  DASHBOARD_MONETIZATION_SALES: '/dashboard/game/:gameCode/monetization/salesOverview',
  DASHBOARD_BASE: '/dashboard/game',
} as const;

/**
 * Custom name mappings for specific metrics
 * 
 * These override the Rainbow API names for better customization.
 * Only define metrics here that you want to customize - all other metrics
 * will automatically fall back to their Rainbow API names.
 * 
 * Naming Strategy:
 * 1. If a metric has a custom name defined → Use custom name
 * 2. If no custom name found → Use Rainbow API name
 * 
 * Works for both PC and Mobile metrics.
 */
export const METRIC_CUSTOM_NAMES: Record<
  GranularityType,
  Record<string, { en: string; cn: string }>
> = {
  daily: {
    // Mobile
    new_users: { en: 'Daily New', cn: 'Daily New' },
    pay_amount: { en: 'Daily Revenue($)', cn: 'Daily Revenue' },
    arpu: { en: 'Daily ARPU($)', cn: 'Daily ARPU($)' },
    payer_rate: { en: 'Daily Payer%', cn: 'Daily Payer%' },
    // PC
    gross_full_game_units: { en: 'Daily Gross Base Game Units', cn: 'Daily Gross Base Game Units' },
    units_number: { en: 'Daily Base Game Units After Refund', cn: 'Daily Base Game Units After Refund' },
    gross_revenue: { en: 'Daily Gross Revenue', cn: 'Daily Gross Revenue' },
    revenue_after_chargeback: { en: 'Daily Revenue After Refund', cn: 'Daily Revenue After Refund' },
    median_online_time: { en: 'Daily Median Playtime', cn: 'Daily Median Playtime' },
    median_session_time: { en: 'Daily Median Session', cn: 'Daily Median Session' },
    pcu: { en: 'Daily PCU', cn: 'Daily PCU' },
  },
  weekly: {
    // Mobile
    new_users: { en: 'Weekly New', cn: 'Weekly New' },
    pay_amount: { en: 'Weekly Revenue($)', cn: 'Weekly Revenue' },
    arpu: { en: 'Weekly ARPU($)', cn: 'Weekly ARPU($)' },
    payer_rate: { en: 'Weekly Payer%', cn: 'Weekly Payer%' },
    // PC
    gross_full_game_units: { en: 'Weekly Gross Base Game Units', cn: 'Weekly Gross Base Game Units' },
    gross_revenue: { en: 'Weekly Gross Revenue', cn: 'Weekly Gross Revenue' },
    mtx_revenue: { en: 'Weekly In-Game Revenue', cn: 'Weekly In-Game Revenue' },
    mtx_arpu: { en: 'Weekly In-Game ARPU', cn: 'Weekly In-Game ARPU' },
    mtx_payer_rate: { en: 'Weekly In-Game Payer%', cn: 'Weekly In-Game Payer%' },
  },
  monthly: {
    // Mobile
    new_users: { en: 'Monthly New', cn: 'Monthly New' },
    pay_amount: { en: 'Monthly Revenue($)', cn: 'Monthly Revenue' },
    arpu: { en: 'Monthly ARPU($)', cn: 'Monthly ARPU($)' },
    payer_rate: { en: 'Monthly Payer%', cn: 'Monthly Payer%' },
    // PC
    gross_full_game_units: { en: 'Monthly Gross Base Game Units', cn: 'Monthly Gross Base Game Units' },
    gross_revenue: { en: 'Monthly Gross Revenue', cn: 'Monthly Gross Revenue' },
    mtx_revenue: { en: 'Monthly In-Game Revenue', cn: 'Monthly In-Game Revenue' },
    mtx_arpu: { en: 'Monthly In-Game ARPU', cn: 'Monthly In-Game ARPU' },
    mtx_payer_rate: { en: 'Monthly In-Game Payer%', cn: 'Monthly In-Game Payer%' },
  },
};

/**
 * Configuration for available metrics by data frame and granularity
 */
export const METRICS_CONFIG: Record<DataFrameType, Record<GranularityType, string[]>> = {
  pc: {
    daily: [ // 17 metrics
      "gross_full_game_units", // Gross Base Game Unit 
      'units_number', // Base Game Units After Refund
      "gross_revenue", // Daily Gross Revenue
      'revenue_after_chargeback', // Revenue After Refund
      "lifetime_full_game_net_units",  // Lifetime Base Game Units After Refund
      "lifetime_revenue_after_refund", // Lifetime Revenue After Refund
      "lifetime_refund_rate", // Lifetime Refund Rate
      "active_users", // DAU
      "new_users", // New
      "median_online_time", // median playtime 
      "median_session_time", // median session time
      "pcu",
      "d1t1",

      // Lifetime Sales page (contains 5 metrics)
      "lifetime_full_game_gross_units",
      "lifetime_gross_revenue",
      // "lifetime_full_game_net_units",  // repeating at daily
      // "lifetime_revenue_after_refund", // repeating at daily
      // "lifetime_net_revenue",

      // Sales Overview Page (contains 10 metrics)
      "full_game_asp",
    ],
    weekly: [ // 16 metrics in total
      'gross_full_game_units',
      'gross_revenue',
      'active_users',
      'new_users',
      'w1t1',
      'mtx_revenue',
      'mtx_arpu',
      'mtx_payer_rate',

      // Sales Overview Page
      // "lifetime_full_game_gross_units",
      // "lifetime_gross_revenue",
      // "gross_full_game_units", // repeating
      // "gross_revenue", // repeating
      // "lifetime_full_game_net_units",
      // "lifetime_revenue_after_refund",
      // "units_number",
      // "revenue_after_chargeback",
      // "full_game_asp",
      // "lifetime_refund_rate",
    ],
    monthly: [ // 16 metrics in total
      'gross_full_game_units',
      'gross_revenue',
      'active_users',
      'new_users',
      'm1t1',
      'mtx_revenue',
      'mtx_arpu',
      'mtx_payer_rate',

      // Sales Overview Page
      // "lifetime_full_game_gross_units",
      // "lifetime_gross_revenue",
      // "gross_full_game_units", // repeating
      // "gross_revenue", // repeating
      // "lifetime_full_game_net_units",
      // "lifetime_revenue_after_refund",
      // "units_number",
      // "revenue_after_chargeback",
      // "full_game_asp",
      // "lifetime_refund_rate",
    ],
  },
  mobile: {
    daily: [
      'active_users',
      'new_users',
      'pay_amount',
      'd2t1',
      'arpu',
      'payer_rate',
    ],
    weekly: [
      'active_users',
      'new_users',
      'pay_amount',
      'w2',
      'arpu',
      'payer_rate',
    ],
    monthly: [
      'active_users',
      'new_users',
      'pay_amount',
      'm2',
      'arpu',
      'payer_rate',
    ],
  },
};

/**
 * Get available metrics for a specific data frame and granularity
 * @param dataFrame - 'pc' or 'mobile'
 * @param granularity - 'daily', 'weekly', or 'monthly'
 * @returns Array of metric keys available for selection
 */
export const getAvailableMetrics = (
  dataFrame: DataFrameType,
  granularity: GranularityType
): string[] => {
  return METRICS_CONFIG[dataFrame]?.[granularity] || [];
};

/**
 * Filter metrics based on data frame configuration
 * @param metrics - Array of metrics from API
 * @param dataFrame - 'pc' or 'mobile'
 * @param granularity - 'daily', 'weekly', or 'monthly'
 * @returns Filtered array of metrics that are allowed for the data frame
 */
export const filterMetricsByDataFrame = (
  metrics: string[],
  dataFrame: DataFrameType,
  granularity: GranularityType
): string[] => {
  const allowedMetrics = getAvailableMetrics(dataFrame, granularity);
  return metrics.filter(metric => allowedMetrics.includes(metric));
};

/**
 * Get custom name for specific metrics
 * Returns custom name if available, otherwise returns undefined
 * @param metricKey - The metric key
 * @param granularity - 'daily', 'weekly', or 'monthly'
 * @param locale - 'en-US' or 'zh-CN'
 * @returns Custom name or undefined if not found
 */
export const getMetricCustomName = (
  metricKey: string,
  granularity: GranularityType,
  locale: string
): string | undefined => {
  const customNames = METRIC_CUSTOM_NAMES[granularity];
  if (!customNames || !customNames[metricKey]) {
    return undefined;
  }
  
  const isZh = locale === 'zh-CN';
  return isZh ? customNames[metricKey].cn : customNames[metricKey].en;
};

